/**
 * Слой объектов на стенах: розетки, выключатели, распредкоробки, смесители.
 *
 * Как выглядит один объект и как он сидит на грани стены, знает WallItemShape2D;
 * слой только решает, кто выделен, кто без кабеля и кому отдавать клики.
 * Прибор без кабеля подсвечивается, пока включён слой проводки; точка водоснабжения
 * без трубы — пока включён слой труб.
 */
import type { PointerEvent } from "react";
import type { Fixture, WallGraph } from "../../plan";
import { getCatalogItem, isWaterPoint, isWiringHub } from "@/shared/catalog";
import { SelectionKind, Tool, useEditorStore } from "@/widgets/editor";
import { useProjectStore } from "@/shared/store";
import { CatalogSectionKind, pipedEndIds, wiredEndIds } from "@/shared/plan";
import { WallItemShape2D } from "./WallItemShape2D";

interface Props {
	graph: WallGraph;
	fixtures: Fixture[];
	mmPerPx: number;
	onFixturePointerDown: (e: PointerEvent<Element>, fixture: Fixture) => void;
}

export const WallItemsLayer2D = ({ graph, fixtures, mmPerPx, onFixturePointerDown }: Props) => {
	const wiring = useProjectStore((s) => s.project.layers.wiring);
	const pipesLayer = useProjectStore((s) => s.project.layers.pipes);
	const selection = useEditorStore((s) => s.selection);
	const tool = useEditorStore((s) => s.tool);

	// Приборы, к которым подходит хоть один кабель, — остальные покажем как неподключённые
	const wired = wiring.visible ? wiredEndIds(wiring.items) : new Set<string>();
	// Точки водоснабжения, к которым подходит хоть одна труба
	const piped = pipesLayer.visible ? pipedEndIds(pipesLayer.items) : new Set<string>();

	return (
		<g>
			{fixtures.map((fixture) => {
				const electrical =
					fixture.ref.catalog === CatalogSectionKind.Electrical ||
					fixture.ref.catalog === CatalogSectionKind.Lighting;
				const info = getCatalogItem(fixture.ref);
				const waterPoint = !electrical && isWaterPoint(info);
				const unconnected = electrical
					? wiring.visible && !isWiringHub(info) && !wired.has(fixture.id)
					: waterPoint && pipesLayer.visible && !piped.has(fixture.id);
				// Кабель ведут только к электрике (и к технике с выводом питания — клик ставит вывод),
				// трубу — к точкам водоснабжения; остальную сантехнику можно лишь выделять.
				// Комментарий привязывают к любому
				const interactive =
					tool === Tool.Select ||
					tool === Tool.Erase ||
					tool === Tool.Comment ||
					(tool === Tool.Cable && Boolean(info?.powerOutlet)) ||
					(electrical
						? tool === Tool.Cable || tool === Tool.LightingConnection
						: waterPoint && tool === Tool.Pipe);
				return (
					<WallItemShape2D
						key={fixture.id}
						graph={graph}
						fixture={fixture}
						mmPerPx={mmPerPx}
						selected={selection?.kind === SelectionKind.Fixture && selection.id === fixture.id}
						unconnected={unconnected}
						interactive={interactive}
						onPointerDown={(e) => onFixturePointerDown(e, fixture)}
					/>
				);
			})}
		</g>
	);
};
